// Eight tiles are staggered by $beat across $cycle; each journey takes ~7s of it.
$cycle: 24s;
$beat: 3s;
// Beat animations are authored with the scan ~0.56s into the beat; tiles are scanned 2.56s into
// their cycle, so shift the beat by -1s (2.56 − 0.56 = 2 ≡ −1 mod 3) to line them up.
$beat-offset: -1s;
// Per-move easing for the tiles, so each kind of motion reads differently.
$land: cubic-bezier(0.22, 1, 0.36, 1); // drop onto the pad: fast, then a soft landing
$glide: cubic-bezier(0.65, 0, 0.35, 1); // travel between stations
$lift: cubic-bezier(0.33, 1, 0.68, 1); // rise into the scan
$pull: cubic-bezier(0.5, 0, 0.75, 0); // drawn into the shredder
$sink: cubic-bezier(0.32, 0, 0.67, 0); // settle into the CDN pad

.wrapper {
  position: relative;
  width: 100%;
  pointer-events: none;
  user-select: none;
}

// Held still until the page has loaded and while off screen (see HeroIso).
.wrapper:not([data-playing]) * {
  animation-play-state: paused !important;
}

// The static backdrop (blurred shadows, the masked map) sizes the graphic and is painted once on
// its own compositing layer. Everything else is a stack of small layers over it (see HeroIso):
// moving parts animate only transform and opacity of HTML layers, which the GPU runs without
// restyling or repainting anything on the main thread.
.backdrop {
  position: relative;
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
  will-change: transform;
}

.layer {
  position: absolute;
}

// Keep the moving layers promoted, so the browser doesn't re-layerize as each one starts and stops.
.tile,
.tileGlow,
.tileTint,
.tileFile,
.morph,
.packet,
.edgeLit,
.strip,
.orbit,
.ring,
.badge,
.badgeScore,
.ringSpin,
.badgeIconAlert {
  will-change: transform, opacity;
}

.art {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.defs {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}

/* Stations */

// Fixed tone steps from one top-left light; brand colour is reserved for the lens and the CDN.
// No outlines: form comes from three tone steps and a soft contact shadow.
.faceTop {
  fill: #fff;
}

// Rounded sides are one silhouette filled by a gradient between the two side tones.
.sideLeftStop {
  stop-color: #e8ebf3;
}

.sideRightStop {
  stop-color: #d8dce8;
}

// Flat dotted world map behind the CDN: fine neutral dots, faded towards the edges.
.mapDots {
  fill: none;
  stroke: color-mix(in srgb, var(--color-primary) 20%, transparent);
  stroke-width: 2.9;
  stroke-linecap: round;
}

.shadowCast {
  fill: #1c2140;
  opacity: 0.1;
}

.shadowContact {
  fill: #1c2140;
  opacity: 0.14;
}

.track {
  stroke: color-mix(in srgb, var(--color-primary) 5%, #f1f2f7);
  stroke-width: 3;
  stroke-linecap: round;
}

/* Purge shredder: an arch cut through the slab; shreds come out of it. */

// Flat tone steps, like every other surface: the inner wall that faces the viewer is a step
// darker than the outer sides.
// The inner wall: a lighter band than the deep floor (before the depth shade lands on it).
.tunnelWall {
  fill: #dadde6;
}

// Depth shade over the slab's own ground shadow, kept soft: light at the entrance, gently
// deeper inside.
.tunnelDepthEntrance {
  stop-color: rgb(28 33 64 / 0.02);
}

.tunnelDepthDeep {
  stop-color: rgb(28 33 64 / 0.08);
}

// The shredder is a shade greyer than the other stations.
.shredTop {
  fill: #eef0f5;
}

// Lighter than the ground in the slab's shadow, so the arch (which shows that shadowed ground)
// reads against the face.
.shredLeftStop {
  stop-color: #e5e7ef;
}

.shredRightStop {
  stop-color: #d0d4df;
}

// Tabler's trash icon laid onto the slab's top face, muted like the globe.
.shredMark {
  color: color-mix(in srgb, var(--color-muted-foreground) 50%, #fff);

  * {
    vector-effect: non-scaling-stroke;
  }
}

// Shreds start inside the shredder and push out through the arch as the tile goes in.
// Flat strips of the flagged asset in its warm tint.
.strip {
  opacity: 0;
  // Keyframes are generated in the component; the name is set inline.
  animation-duration: $cycle;
  animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  animation-iteration-count: infinite;
}

.stripTop {
  fill: color-mix(in srgb, var(--color-warning) 12%, #fff);
}

/* Upload landing pad */

.landingFill {
  fill: color-mix(in srgb, var(--color-primary) 5%, var(--color-background));
}

.landingOutline {
  fill: none;
  stroke: color-mix(in srgb, var(--color-primary) 45%, var(--color-background));
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
  stroke-dasharray: 0.1 7;
}

/* Lens */

.lensTop {
  fill: color-mix(in srgb, var(--color-primary) 82%, #fff);
}

.lensLogo {
  fill: rgb(255 255 255 / 0.92);
}

// The ring shows only while an asset is on the lens, on the badges' clock (see badgeIdle).
.ring {
  opacity: 0.6;
  animation: badgeIdle $beat ease-in-out infinite;
  animation-delay: $beat-offset;
}

.ringStroke {
  fill: none;
  stroke: color-mix(in srgb, var(--color-primary) 35%, transparent);
  stroke-width: 1.5;
  stroke-dasharray: 2 5;
  stroke-linecap: round;
}

// The orbit and its badges pulse together, scaling about the ring's centre (set inline).
.orbit {
  animation: ringPulse $beat $glide infinite;
  animation-delay: $beat-offset;
}

// The pulse grows while the score rings fill (tile time 2.57–3.05s → 19–35% of the beat after
// $beat-offset), so the orbit and the rings move together.
@keyframes ringPulse {
  0%,
  19% {
    transform: scale(1);
  }

  35% {
    transform: scale(1.03);
  }

  52%,
  100% {
    transform: scale(1);
  }
}

/* Category badges: a small version of the app's category score ring. */

// Badges are hidden between assets, fade in the moment an asset lands on the lens, and fade out
// fully once its result has cleared (same beat alignment as the sweep).
.badge {
  animation: badgeIdle $beat ease-in-out infinite;
}

// The asset lands on the lens ~8% into the beat; the badges light up right then.
@keyframes badgeIdle {
  0%,
  6.5% {
    opacity: 0;
  }

  10.5%,
  64% {
    opacity: 1;
  }

  72%,
  100% {
    opacity: 0;
  }
}

// A flat offset disc stands in for a drop shadow, which would re-blur on every frame of the pulse.
.badgeShadow {
  fill: rgb(28 33 64 / 0.07);
}

.badgeDisc {
  fill: var(--color-card);
}

.badgeTrack {
  fill: none;
  stroke: color-mix(in srgb, var(--color-primary) 10%, #d6dae6);
  stroke-width: 1.8;
}

// Each asset's ring, on that asset's clock: fills to its score as it's scanned, holds briefly,
// then clears before the next asset reaches the lens. Green unless it's the flagged area.
// The ring fades; inside it, half-rings rotate out from under half-width clips to fill it.
.badgeScore {
  opacity: 0;
  // Starts moving immediately, so the fill lines up with the orbit's pulse.
  animation: badgeScore $cycle $lift infinite;
}

.ringArc {
  fill: none;
  stroke: color-mix(in srgb, var(--color-success) 60%, #fff);
  stroke-width: 1.8;
  stroke-linecap: round;
}

.ringCap {
  fill: color-mix(in srgb, var(--color-success) 60%, #fff);
}

.badgeScoreFlagged {
  .ringArc {
    stroke: color-mix(in srgb, var(--color-danger) 70%, #fff);
  }

  .ringCap {
    fill: color-mix(in srgb, var(--color-danger) 70%, #fff);
  }
}

.ringHalf {
  overflow: hidden;
}

// Keyframes are generated per score in the component; the name is set inline.
.ringSpin {
  transform-origin: 50% 50%;
  animation-duration: $cycle;
  animation-timing-function: $lift;
  animation-iteration-count: infinite;
}

// Scores past half fill with two halves, sampled from the same eased sweep.
.ringSpinSampled {
  animation-timing-function: linear;
}

// Lands on the lens at 9.3%, scanned 10.7–13%, next asset lands 12.5% later.
@keyframes badgeScore {
  0%,
  10.6% {
    opacity: 0;
  }

  10.7%,
  15.5% {
    opacity: 1;
  }

  17.5%,
  100% {
    opacity: 0;
  }
}

.badgeIcon,
.badgeIconAlertColor {
  color: var(--color-muted-foreground);

  svg {
    overflow: visible;
  }
}

.badgeIconAlertColor {
  color: color-mix(in srgb, var(--color-danger) 80%, #000);
}

.badgeIconAlert {
  opacity: 0;
  animation: badgeIconAlert $cycle ease-out infinite;
}

@keyframes badgeIconAlert {
  0%,
  11% {
    opacity: 0;
  }

  12.2%,
  15.5% {
    opacity: 1;
  }

  17.5%,
  100% {
    opacity: 0;
  }
}

/* CDN delivery: copies travel the ground tracks to edge locations, which light as they arrive. */

// Tabler's globe icon (the app's icon set), laid onto the pad's top face.
.globeMark {
  color: color-mix(in srgb, var(--color-muted-foreground) 50%, #fff);

  * {
    vector-effect: non-scaling-stroke;
  }
}

.route {
  fill: none;
  stroke: color-mix(in srgb, var(--color-primary) 7%, #e6e8f0);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

// Keyframes are generated per route in the component (constant speed); names and delays are inline.
.packet {
  opacity: 0;
  // The animation name is set inline per route (keyframes generated in the component).
  animation-duration: $cycle;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

// The whole node turns primary on arrival, keeping the same three-tone shading.
.packetStroke {
  fill: none;
  stroke: color-mix(in srgb, var(--color-primary) 70%, #fff);
  stroke-width: 3;
  stroke-linecap: round;
}

.edgeLit {
  opacity: 0;
  animation-duration: $cycle;
  animation-timing-function: ease-out;
  animation-iteration-count: infinite;
}

.litTop {
  fill: color-mix(in srgb, var(--color-primary) 16%, #fff);
}

.litLeftStop {
  stop-color: color-mix(in srgb, var(--color-primary) 24%, #e8ebf3);
}

.litRightStop {
  stop-color: color-mix(in srgb, var(--color-primary) 32%, #d8dce8);
}

/* Tiles */

// Keyframes (approved or flagged) are generated in the component; the name is set inline.
.tile {
  transform-origin: center;
  animation-duration: $cycle;
  animation-timing-function: $glide;
  animation-iteration-count: infinite;
}

.tileLeftStop {
  stop-color: #e3e6f1;
}

.tileRightStop {
  stop-color: #cdd2e4;
}

// Flagged tiles: the sides take the same warm tint as the top, in the side tones.
.tileFlagLeftStop {
  stop-color: color-mix(in srgb, var(--color-warning) 16%, #e3e6f1);
}

.tileFlagRightStop {
  stop-color: color-mix(in srgb, var(--color-warning) 20%, #cdd2e4);
}

.tileSafeLeftStop {
  stop-color: color-mix(in srgb, var(--color-success) 14%, #e3e6f1);
}

.tileSafeRightStop {
  stop-color: color-mix(in srgb, var(--color-success) 18%, #cdd2e4);
}

// The verdict tint cross-fades in over the plain tile, the moment the lens clears it.
.tileTint {
  opacity: 0;
  animation: flagSide $cycle ease-out infinite;
}

@keyframes flagSide {
  0%,
  13.3% {
    opacity: 0;
  }

  14.1%,
  100% {
    opacity: 1;
  }
}

.tileTop {
  fill: #fff;
}

.glyph {
  fill: none;
  stroke: color-mix(in srgb, var(--color-muted-foreground) 55%, #fff);
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;

  * {
    vector-effect: non-scaling-stroke;
  }
}

// Approved tiles turn green once the lens clears them, at the same moment flagged ones turn amber.
.tintTop {
  fill: color-mix(in srgb, var(--color-success) 11%, #fff);
}

.tintGlyph {
  stroke: color-mix(in srgb, var(--color-success) 30%, #b3b9c3);
}

.tileFlagged {
  .tintTop {
    fill: color-mix(in srgb, var(--color-warning) 13%, #fff);
  }

  .tintGlyph {
    stroke: color-mix(in srgb, var(--color-warning) 30%, #b3b9c3);
  }
}

// CalmLens transforms approved files into new shapes outside the CDN pad. Keyframes (the file giving way, each
// morph frame fading in over the last) are generated in the component; the names are set inline.
// Each frame mixes the verdict's green into the brand colour by its progress (--morph).
.tileFile,
.morph {
  animation-duration: $cycle;
  animation-timing-function: ease-out;
  animation-iteration-count: infinite;
}

.morph {
  opacity: 0;
}

// The selection outline and corner handles round a file while CalmLens transforms it.
.morphOutline {
  fill: color-mix(in srgb, var(--color-primary) 6%, transparent);
  stroke: var(--color-primary);
  stroke-width: 1;
  stroke-dasharray: 3 2.5;
}

.morphHandle {
  fill: #fff;
  stroke: var(--color-primary);
  stroke-width: 1.2;
}

.morphTop {
  fill: color-mix(
    in srgb,
    color-mix(in srgb, var(--color-primary) 22%, #fff) var(--morph),
    color-mix(in srgb, var(--color-success) 11%, #fff)
  );
}

.morphLeftStop {
  stop-color: color-mix(
    in srgb,
    color-mix(in srgb, var(--color-primary) 42%, #e8ebf3) var(--morph),
    color-mix(in srgb, var(--color-success) 14%, #e3e6f1)
  );
}

.morphRightStop {
  stop-color: color-mix(
    in srgb,
    color-mix(in srgb, var(--color-primary) 56%, #d8dce8) var(--morph),
    color-mix(in srgb, var(--color-success) 18%, #cdd2e4)
  );
}

/* Captions */

.leader {
  stroke: var(--color-primary);
  stroke-width: 1.5;
  stroke-dasharray: 0.5 3.5;
  stroke-linecap: round;
}

.leaderDot {
  fill: var(--color-primary);
}

.label {
  fill: var(--color-foreground);
  font-size: 16px;
  font-weight: 600;
}

.sublabel {
  fill: var(--color-muted-foreground);
  font-size: 13.5px;
}

.step {
  fill: var(--color-muted-foreground);
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.8em;
  font-weight: 500;
}

// The diagram scales with its container; on phones keep the stage names legible and drop the detail line.
@media (max-width: 640px) {
  .label {
    font-size: 26px;
  }

  .sublabel {
    display: none;
  }
}

/* Reduced motion: one still frame showing every stage at once. */

@media (prefers-reduced-motion: reduce) {
  .wrapper * {
    animation: none !important;
  }

  .tile:not([data-still]) {
    opacity: 0;
  }

  // The still position is set inline per tile.
  .tile[data-still] {
    transform: var(--still, none);
  }

  .tileTint {
    opacity: 1;
  }

  // The tile held outside the CDN pad shows the file already transformed into its final shape.
  .tile[data-still="deliver"] {
    .tileFile {
      opacity: 0;
    }

    .morph:last-child {
      opacity: 1;
    }
  }

  // Approved tiles show only their tinted sides (the tinted copy's top and mark stay plain, since
  // its side silhouette covers the top); flagged ones the full warning tint.
  .tile:not(.tileFlagged) .tintTop {
    fill: #fff;
  }

  .tile:not(.tileFlagged) .tintGlyph {
    stroke: var(--color-muted-foreground);
  }

  .tileFlagged .tintTop {
    fill: color-mix(in srgb, var(--color-warning) 14%, var(--color-card));
  }

  .tileFlagged .tintGlyph {
    stroke: var(--color-warning);
  }
}
